import "./SurgicalPricingModal.scss";

import { MaskedInput, Select } from "components/inputs";
import { Tooltip } from "components/Tooltip/Tooltip";
import debounce from "lodash/debounce";
import { useMemo, useState } from "react";
import type { Control, FieldErrors, UseFormSetValue } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useGetAnesthesiaCodesByInputQuery } from "services/codes-api";

type AnesthesiaOption = {
  readonly label: string;
  readonly value: string;
  readonly __isNew__?: boolean;
  readonly base_unit?: number;
  readonly description?: string;
};

export interface AnesthesiaFormFields {
  anesthesia_code: string;
  anesthesia_description: string;
  base_unit: number | null;
  total_units: number | null;
  conversion_factor: number | null;
}

type AnesthesiaSectionProps = {
  control: Control<AnesthesiaFormFields>;
  errors: FieldErrors<AnesthesiaFormFields>;
  setValue: UseFormSetValue<AnesthesiaFormFields>;
};

const AnesthesiaSection = ({
  control,
  errors,
  setValue,
}: AnesthesiaSectionProps) => {
  const [codeSearchTerm, setCodeSearchTerm] = useState<string | null>(null);
  const [descSearchTerm, setDescSearchTerm] = useState<string | null>(null);

  const debouncedSetCodeSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setCodeSearchTerm(value);
      }, 500),
    [],
  );

  const debouncedSetDescSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setDescSearchTerm(value);
      }, 500),
    [],
  );

  const { data: codeResults, isFetching: isCodeFetching } =
    useGetAnesthesiaCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm?.match(/^\d/) ? codeSearchTerm : null,
        description__icontains: !codeSearchTerm?.match(/^\d/)
          ? codeSearchTerm
          : null,
        limit: 25,
      },
      {
        skip: !codeSearchTerm,
      },
    );

  const { data: descResults, isFetching: isDescFetching } =
    useGetAnesthesiaCodesByInputQuery(
      {
        description__icontains: descSearchTerm,
        limit: 25,
      },
      {
        skip: !descSearchTerm,
      },
    );

  const codeOptions = useMemo(() => {
    if (!codeResults?.results) return [];

    return (
      codeResults.results as Array<{
        code: string;
        description?: string;
        base_unit: number;
      }>
    ).map((result) => ({
      label: `${result.code} - ${result.description || "No description available"}`,
      value: result.code,
      base_unit: Number(result.base_unit),
      description: result.description,
    }));
  }, [codeResults]);

  const descOptions = useMemo(() => {
    if (!descResults?.results) return [];

    return (
      descResults.results as Array<{
        code: string;
        description?: string;
        base_unit: number;
      }>
    ).map((result) => ({
      label: `${result.code} - ${result.description || "No description available"}`,
      value: result.code,
      base_unit: Number(result.base_unit),
      description: result.description,
    }));
  }, [descResults]);

  return (
    <>
      <div className="cpt-codes-section">
        <div className="header-primary">Anesthesia Code</div>
        <div className="header-primary">Description</div>
        <div className="header-primary">Base Unit</div>
        <div className="header-primary"></div>

        {(() => {
          const codeSelect = (
            <Controller
              name="anesthesia_code"
              control={control}
              render={({ field: { onChange, value, ...field } }) => {
                const codeSelect = (
                  <Select
                    {...field}
                    options={codeOptions}
                    value={
                      value
                        ? {
                            label: `${value} - ${control._formValues.base_unit || "No base unit"}`,
                            value,
                          }
                        : null
                    }
                    errors={errors}
                    placeholder="Search anesthesia code..."
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue
                        ? "Begin typing an anesthesia code..."
                        : isCodeFetching
                          ? "Searching..."
                          : "No matches found."
                    }
                    onInputChange={(value: string) =>
                      debouncedSetCodeSearchTerm(value)
                    }
                    onChange={(option) => {
                      const selected = option as AnesthesiaOption;
                      if (!selected) {
                        onChange("");
                        control._formValues.base_unit = null;
                        control._formValues.description = "";
                        setValue("base_unit", null);
                        setValue("anesthesia_description", "");
                        return;
                      }
                      onChange(selected.value);
                      control._formValues.base_unit =
                        selected.base_unit || null;
                      control._formValues.description =
                        selected.description || "";
                      setValue("base_unit", selected.base_unit || null);
                      setValue(
                        "anesthesia_description",
                        selected.description || "",
                      );
                    }}
                  />
                );

                return value ? (
                  <Tooltip
                    content={`${value} - ${control._formValues.base_unit || "No base unit"}`}
                    className="tooltip-full-width"
                  >
                    {codeSelect}
                  </Tooltip>
                ) : (
                  codeSelect
                );
              }}
            />
          );

          return codeSelect;
        })()}

        {(() => {
          const descSelect = (
            <Controller
              name="anesthesia_description"
              control={control}
              render={({ field: { onChange, value, ...field } }) => {
                const descSelect = (
                  <Select
                    {...field}
                    options={descOptions}
                    value={
                      value
                        ? {
                            label: value,
                            value: value,
                          }
                        : null
                    }
                    errors={errors}
                    placeholder="Search description..."
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue
                        ? "Begin typing a description..."
                        : isDescFetching
                          ? "Searching..."
                          : "No matches found."
                    }
                    onInputChange={(value: string) =>
                      debouncedSetDescSearchTerm(value)
                    }
                    onChange={(option) => {
                      const selected = option as AnesthesiaOption;
                      if (!selected) {
                        onChange("");
                        setValue("anesthesia_code", "");
                        setValue("base_unit", null);
                        return;
                      }
                      onChange(selected.description || "");
                      setValue("anesthesia_code", selected.value);
                      setValue("base_unit", selected.base_unit || null);
                    }}
                  />
                );

                return value ? (
                  <Tooltip content={value} className="tooltip-full-width">
                    {descSelect}
                  </Tooltip>
                ) : (
                  descSelect
                );
              }}
            />
          );

          return descSelect;
        })()}

        <Controller
          name="base_unit"
          control={control}
          rules={{
            validate: (value) => {
              if (value !== null && (isNaN(value) || value < 0)) {
                return "Base unit must be a positive number";
              }
              return true;
            },
          }}
          render={({ field: { value, ...field } }) => (
            <MaskedInput
              {...field}
              mask="float"
              value={value || ""}
              errors={errors}
              readOnly={true}
            />
          )}
        />
        <div></div>
      </div>

      <div className="border mrg-bottom"></div>

      <Controller
        name="total_units"
        control={control}
        rules={{
          validate: (value) => {
            if (value !== null && (isNaN(value) || value < 0)) {
              return "Total units must be a positive number";
            }
            return true;
          },
        }}
        render={({ field: { value, onChange, ...field } }) => (
          <MaskedInput
            {...field}
            mask="number"
            value={value || ""}
            onChange={(e) =>
              onChange(e.target.value ? Number(e.target.value) : null)
            }
            label="Total Units"
            errors={errors}
            maxLength={8}
            tooltip="e.g. 1 unit = 15 minutes; 8 units = 2 hours"
          />
        )}
      />

      <Controller
        name="conversion_factor"
        control={control}
        rules={{
          validate: (value) => {
            if (value !== null && (isNaN(value) || value < 0)) {
              return "Conversion factor must be a positive number";
            }
            return true;
          },
        }}
        render={({ field: { value, onChange, ...field } }) => (
          <MaskedInput
            {...field}
            mask="float"
            value={value || ""}
            onChange={(e) =>
              onChange(e.target.value ? Number(e.target.value) : null)
            }
            label="Conversion Factor"
            errors={errors}
          />
        )}
      />
    </>
  );
};

export default AnesthesiaSection;
